'use client';
import {
  useEffect,
  createContext,
  ReactNode,
  useState,
  useContext,
  useMemo,
} from 'react';
import * as amplitude from '@amplitude/analytics-browser';

export type AmplitudeContextProviderProps = {
  apiKey?: string;
  appVersion: string;
  children: ReactNode;
};

export type AmplitudeLibType = typeof amplitude;
export type AmplitudeContextType = AmplitudeLibType & {
  initialized: boolean;
};

export const AmplitudeContext = createContext<AmplitudeContextType>({
  initialized: false,
  ...amplitude,
});

export const useAmplitude = () => {
  const context = useContext(AmplitudeContext);

  if (context === undefined) {
    throw new Error(
      'useAmplitudeContext must be used within a AmplitudeContextProvider',
    );
  }

  return context;
};

export const AmplitudeContextProvider = ({
  apiKey,
  appVersion,
  children,
}: AmplitudeContextProviderProps) => {
  const [initialized, setInitialized] = useState(false);

  // Make sure to init only once, on the client-side.
  useEffect(() => {
    if (initialized || !apiKey) {
      return;
    }

    amplitude.init(apiKey, {
      appVersion,
      serverZone: 'EU', // force the zone to EU (otherwise default is US).
      autocapture: true,
    });

    setInitialized(true);
  }, []);

  const value = useMemo(() => {
    return {
      initialized,
      ...amplitude,
    };
  }, [initialized, amplitude]);

  return (
    <AmplitudeContext.Provider value={value}>
      {children}
    </AmplitudeContext.Provider>
  );
};
